import * as React from 'react'

import { DAY_TYPES } from 'config/Vars'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import { useFormError } from 'modules/timeoff/hooks/useForm'
import type AllowanceIncidenceForm from 'modules/timeoff/lib/AllowanceIncidence'
import { validateLessThanThreeDecimals } from 'modules/timeoff/lib/validateDecimals'

interface Props {
  form: AllowanceIncidenceForm
}

function DaysForm({ form }: Props) {
  const setFormError = useFormError()
  React.useEffect(() => {
    return reaction(
      () => form.get('day_type').value,
      (dayType) => {
        const sign = Number(form.get('sign').value)
        if (dayType === DAY_TYPES.half_day) {
          form.get('days_in_cents').mapAndSet(50 * sign)
        }
      }
    ) // We only need to setup the reaction on mount and to clean it up on
    // unmount, we don't want it to run on the changes of `form`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  React.useEffect(() => {
    return reaction(
      () => form.get('days_in_cents').value,
      (daysInCents) => {
        const amount = Number(daysInCents)
        if (!isNaN(amount)) {
          form.get('sign').mapAndSet(amount < 0 ? '-1' : '1')
        }
      }
    )
  }, [])

  const validateDecimals = () => {
    return validateLessThanThreeDecimals(form, setFormError)
  }

  return (
    <>
      <FormRow extra={{ 'data-intercom-target': 'timeoff-incidences-day-type-input' }}>
        <RadioGroup
          invisibleRadio
          name="dayType"
          theme="toggle"
          field={form.get('day_type')}
          options={[
            {
              value: DAY_TYPES.half_day,
              label: i18n.t('time_off.day_types.half_day'),
            },
            {
              value: DAY_TYPES.days,
              label: i18n.t('time_off.day_types.days'),
            },
          ]}
        />
      </FormRow>
      <FormRow>
        <Text
          readonly={form.get('day_type').value === DAY_TYPES.half_day}
          label={i18n.t('time_off.incidences.field_days')}
          field={form.get('days_in_cents')}
          type="number"
          validation={validateDecimals}
          extra={{ 'data-intercom-target': 'timeoff-incidences-days-input' }}
        />
      </FormRow>
    </>
  )
}

export default observer(DaysForm)
